import * as React from 'react';
import cx from 'clsx';
import dayjs from 'dayjs';
import { ApolloError } from '@apollo/client/errors';
import { ArrowDownIcon, HeartIcon } from '@heroicons/react/outline';
import { Badge, Icon, Spinner } from '@vechaiui/react';
import { useLazyQuery } from '@apollo/client';
import { useLocalization } from '@fluent/react';

import {
  CalcRfqPriceDocument,
  ContainerPacking,
  RequestForQuotation,
} from '#/__generated__/graphql/graphql';
import { formatAddress, formatMoney } from '#/utils/formatter';

const TripSummaryPanel: React.FC<TripSummaryPanelProps> = ({ rfq, onError }) => {
  const { l10n } = useLocalization();
  const firstLeg = rfq.routeLegs[0];
  const [
    executeGetRfqPriceQuery,
    { data: rfqPriceData, error: rfqPriceError, loading: rfqPriceLoading },
  ] = useLazyQuery(CalcRfqPriceDocument);

  React.useEffect(() => {
    if (
      rfq.preferredVehicle?.name &&
      rfq.routeLegs.every(
        (leg) =>
          !!leg.startLocation.address.addressLocality && !!leg.endLocation.address.addressLocality,
      )
    ) {
      executeGetRfqPriceQuery({
        variables: {
          preferredVehicle: rfq.preferredVehicle,
          routeLegs: rfq.routeLegs.map((route) => ({
            startLocation: route.startLocation,
            endLocation: route.endLocation,
          })),
        },
      });
    }
  }, [executeGetRfqPriceQuery, rfq.preferredVehicle, rfq.routeLegs]);

  React.useEffect(() => {
    onError?.(rfqPriceError);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rfqPriceError]);

  return (
    <div>
      <header>
        <h3 className="text-2xl font-semibold text-black">
          {l10n.getString('trip')} {l10n.getString('summary')}
        </h3>
      </header>

      <div className="p-6 mt-3 bg-white border 2xl:p-8 border-neutral-400 rounded-xl">
        <div className="text-xs uppercase text-neutral-400">{l10n.getString('from')}</div>
        <TransportInfo
          title={
            formatAddress(firstLeg.startLocation.address, {
              exclude: ['addressCountry', 'streetAddress', 'postalCode'],
            }) || '-'
          }
          value={!!rfq.pickupTime ? dayjs(rfq.pickupTime).format('lll') : ''}
        />

        <div className="my-5 space-x-3">
          <Icon as={ArrowDownIcon} label="check" className="w-6 h-6 text-primary-500" />
          {!!rfq.pickupTime && !!rfq.deliveryTime && (
            <Badge>{dayjs(rfq.pickupTime).from(rfq.deliveryTime, true)}</Badge>
          )}
          {/* {!!stopCount && (
            <Button
              size="md"
              type="button"
              className="border-gray-300 rounded-full text-neutral-400"
            >
              {stopCount} {l10n.getString('stop')}
            </Button>
          )} */}
        </div>

        <div className="text-xs uppercase text-neutral-400">{l10n.getString('to')}</div>
        <TransportInfo
          title={
            formatAddress(firstLeg.endLocation.address, {
              exclude: ['addressCountry', 'streetAddress', 'postalCode'],
            }) || '-'
          }
          value={!!rfq.deliveryTime ? dayjs(rfq.deliveryTime).format('lll') : ''}
        />

        <div className="flex flex-1 gap-2 pt-4 mt-8 border-t border-gray-300">
          {rfq.preferredContainerPacking !== ContainerPacking.LessThanContainerLoad && (
            <InfoSection
              title={l10n.getString('vehicle-type')}
              value={rfq.preferredVehicle?.name || '-'}
            />
          )}
          <div
            className={cx(
              'flex-1 pl-2 border-gray-300',
              rfq.preferredContainerPacking !== ContainerPacking.LessThanContainerLoad &&
                'border-l',
            )}
          >
            <InfoSection
              title={l10n.getString('weight')}
              value={`${rfq.cargoDetails.cargoWeight.value ?? 0} ${l10n.getString('kilogram', {
                value: rfq.cargoDetails.cargoWeight.value ?? 0,
              })}`}
            />
          </div>
        </div>
        <div>
          <div className="p-4 mt-5 rounded-lg bg-neutral-100">
            <div className="flex items-center justify-center space-x-1">
              <div>{l10n.getString('total')}:</div>
              <div>
                {rfqPriceLoading && <Spinner />}
                {!rfqPriceLoading && (
                  <span className="font-semibold">
                    {rfqPriceData
                      ? formatMoney(rfqPriceData.calculateRequestForQuotationPrice)
                      : '-'}
                  </span>
                )}
              </div>
            </div>
          </div>
          {!!rfqPriceError && <p className="mt-2 text-sm text-red-700">{rfqPriceError.message}</p>}
        </div>
        {!!rfq.insuranceIncluded && (
          <div className="flex justify-between mt-3">
            <div className="flex">
              <Icon as={HeartIcon} label="check" className="w-5 h-5 text-primary-500" />
              <div className="ml-3 text-sm text-neutral-500">
                {l10n.getString('with-insurance-coverage')}
              </div>
            </div>
            {/* <Icon as={InformationCircleIcon} label="check" className="w-5 h-5 text-primary-500" /> */}
          </div>
        )}
      </div>
    </div>
  );
};
export type TripSummaryPanelProps = {
  rfq: RequestForQuotation;
  onError: (error: ApolloError | undefined) => void;
};

const InfoSection = ({ title, value }: { title: string; value: string }) => {
  return (
    <div className="content-center flex-1">
      <div className="text-xs">{title}</div>
      <div className="text-lg">{value}</div>
    </div>
  );
};

const TransportInfo = ({ title, value }: { title: string; value: string }) => {
  return (
    <div className="py-2">
      <div className="text-xl font-semibold text-black">{title}</div>
      <div className="text-lg text-neutral-400">{value}</div>
    </div>
  );
};

export default TripSummaryPanel;
